import { encodeFunctionData, erc20Abi } from "viem";
import { POLYMARKET_CHAIN_ID, POLYMARKET_COLLATERAL_OFFRAMP, POLYMARKET_DEPOSIT_WALLET_FACTORY, POLYMARKET_OFFRAMP_ABI, POLYMARKET_PUSD_ADDRESS, POLYMARKET_USDC_E_ADDRESS, } from "./constants";
/**
 * Deposit-wallet (modern ERC-1967 funder) gasless batch, in pure viem — no
 * @polymarket SDK. Port of the SDK's deposit-wallet scheme: EIP-712 typed-data
 * (`Batch`) signature over an ordered list of calls + nonce + deadline. The deposit
 * wallet executes each call as `msg.sender`; the relayer broadcasts it gaslessly.
 */
const DEPOSIT_WALLET_DOMAIN_NAME = 'DepositWallet';
const DEPOSIT_WALLET_DOMAIN_VERSION = '1';
const DEPOSIT_WALLET_TYPES = {
    Call: [
        { name: 'target', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'data', type: 'bytes' },
    ],
    Batch: [
        { name: 'wallet', type: 'address' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
        { name: 'calls', type: 'Call[]' },
    ],
};
/** Relayer request that deploys the user's deposit wallet (gasless, no signature). */
export function buildDepositWalletDeployRequest(fromEoa) {
    return { type: 'WALLET-CREATE', from: fromEoa, to: POLYMARKET_DEPOSIT_WALLET_FACTORY };
}
export async function buildDepositWalletBatchRequest(params) {
    const calls = params.calls.map(c => ({ target: c.target, value: c.value ?? 0n, data: c.data }));
    const signature = await params.walletClient.signTypedData({
        account: params.fromEoa,
        domain: {
            name: DEPOSIT_WALLET_DOMAIN_NAME,
            version: DEPOSIT_WALLET_DOMAIN_VERSION,
            chainId: POLYMARKET_CHAIN_ID,
            verifyingContract: params.depositWallet,
        },
        types: DEPOSIT_WALLET_TYPES,
        primaryType: 'Batch',
        message: {
            wallet: params.depositWallet,
            nonce: BigInt(params.nonce),
            deadline: BigInt(params.deadline),
            calls,
        },
    });
    return {
        type: 'WALLET',
        from: params.fromEoa,
        to: POLYMARKET_DEPOSIT_WALLET_FACTORY,
        nonce: params.nonce,
        signature,
        depositWalletParams: {
            depositWallet: params.depositWallet,
            deadline: params.deadline,
            calls: calls.map(c => ({ target: c.target, value: c.value.toString(), data: c.data })),
        },
    };
}
/**
 * Flow 2 calls for the deposit-wallet batch:
 *   pUSD funder → [approve pUSD→offramp, unwrap pUSD→USDC.e, approve USDC.e→depository, depositERC20]
 *   USDC.e funder → [approve USDC.e→depository, depositERC20]
 * The deposit leg reuses the backend `call_data`/`to_address` verbatim (no client-side encoding).
 */
export function buildPolymarketDepositCalls(params) {
    const { funderTokenAddress, funderAddress, amountBaseUnits, depository, depositCallData } = params;
    const approveDepository = {
        target: POLYMARKET_USDC_E_ADDRESS,
        data: encodeFunctionData({ abi: erc20Abi, functionName: 'approve', args: [depository, amountBaseUnits] }),
    };
    const deposit = { target: depository, data: depositCallData };
    const holdsPusd = funderTokenAddress.toLowerCase() === POLYMARKET_PUSD_ADDRESS.toLowerCase();
    if (!holdsPusd)
        return [approveDepository, deposit];
    return [
        {
            target: POLYMARKET_PUSD_ADDRESS,
            data: encodeFunctionData({ abi: erc20Abi, functionName: 'approve', args: [POLYMARKET_COLLATERAL_OFFRAMP, amountBaseUnits] }),
        },
        {
            target: POLYMARKET_COLLATERAL_OFFRAMP,
            data: encodeFunctionData({ abi: POLYMARKET_OFFRAMP_ABI, functionName: 'unwrap', args: [POLYMARKET_USDC_E_ADDRESS, funderAddress, amountBaseUnits] }),
        },
        approveDepository,
        deposit,
    ];
}
